'use client'

import React from 'react'
import { cn, openInNewTab } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'

// ---------------------------------------------------------------------------
// Entity type metadata — icon and colour accent per entity type
// ---------------------------------------------------------------------------

type EntityType =
  | 'claim'
  | 'sample'
  | 'rfi'
  | 'da_worksheet'
  | 'rebuttal'
  | 'dispute'
  | 'universe'
  | 'workbench'
  | 'dashboard'
  | 'reports'
  | 'settings'
  | 'unknown'

interface EntityMeta {
  icon: React.ElementType
  accent: string   // Tailwind bg/border classes
  label: string    // Fallback type label
}

const ENTITY_META: Record<EntityType, EntityMeta> = {
  claim:        { icon: Icons.fileText,      accent: 'border-brand-cornflower/40 bg-brand-cornflower/8 hover:bg-brand-cornflower/15', label: 'Claim' },
  sample:       { icon: Icons.layers,        accent: 'border-brand-purple/40 bg-brand-purple/8 hover:bg-brand-purple/15',           label: 'Sample' },
  rfi:          { icon: Icons.alertTriangle, accent: 'border-amber-400/40 bg-amber-50 hover:bg-amber-100',                           label: 'RFI' },
  da_worksheet: { icon: Icons.pencil,        accent: 'border-orange-400/40 bg-orange-50 hover:bg-orange-100',                        label: 'DA' },
  rebuttal:     { icon: Icons.repeat,        accent: 'border-rose-400/40 bg-rose-50 hover:bg-rose-100',                              label: 'Rebuttal' },
  dispute:      { icon: Icons.shield,        accent: 'border-red-400/40 bg-red-50 hover:bg-red-100',                                 label: 'Dispute' },
  universe:     { icon: Icons.inbox,         accent: 'border-teal-400/40 bg-teal-50 hover:bg-teal-100',                              label: 'Universe' },
  workbench:    { icon: Icons.workbench,     accent: 'border-brand-cornflower/40 bg-brand-cornflower/8 hover:bg-brand-cornflower/15', label: 'Workbench' },
  dashboard:    { icon: Icons.dashboard,     accent: 'border-brand-navy/40 bg-brand-navy/5 hover:bg-brand-navy/10',                  label: 'Dashboard' },
  reports:      { icon: Icons.barChart,      accent: 'border-emerald-400/40 bg-emerald-50 hover:bg-emerald-100',                     label: 'Reports' },
  settings:     { icon: Icons.settings,      accent: 'border-gray-400/40 bg-gray-50 hover:bg-gray-100',                              label: 'Settings' },
  unknown:      { icon: Icons.fileText,      accent: 'border-brand-cornflower/30 bg-brand-cornflower/5 hover:bg-brand-cornflower/10', label: 'Entity' },
}

// ---------------------------------------------------------------------------
// Derive entity type from an internal href
// ---------------------------------------------------------------------------

function inferEntityType(href: string): EntityType {
  if (href.startsWith('/claims'))       return 'claim'
  if (href.startsWith('/samples'))      return 'sample'
  if (href.startsWith('/rfis'))         return 'rfi'
  if (href.includes('tab=rebuttals'))   return 'rebuttal'
  if (href.includes('tab=disputes'))    return 'dispute'
  if (href.startsWith('/disagreements')) return 'da_worksheet'
  if (href.startsWith('/universes'))    return 'universe'
  if (href.startsWith('/workbench'))    return 'workbench'
  if (href === '/')                     return 'dashboard'
  if (href.startsWith('/reports'))      return 'reports'
  if (href.startsWith('/settings'))     return 'settings'
  return 'unknown'
}

// ---------------------------------------------------------------------------
// EntityChip — the public component
// ---------------------------------------------------------------------------

interface EntityChipProps {
  /** Display text (e.g., claim number or sample name). */
  label: string
  /** Internal frontend href (e.g., /claims/uuid). */
  href: string
  /** Optional override for entity type; inferred from href if omitted. */
  entityType?: EntityType
  /** Called when the eye (preview) icon is clicked. */
  onPreview?: (href: string, label: string, entityType: EntityType) => void
  /** Extra class names */
  className?: string
}

export function EntityChip({
  label,
  href,
  entityType,
  onPreview,
  className,
}: EntityChipProps) {
  const type = entityType ?? inferEntityType(href)
  const meta = ENTITY_META[type] ?? ENTITY_META.unknown
  const Icon = meta.icon

  const handleNavigate = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Option B: open in new tab — conversation stays persistent
    openInNewTab(href)
  }

  const handlePreview = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onPreview?.(href, label, type)
  }

  return (
    <span
      className={cn(
        // Base pill layout
        'inline-flex items-center gap-1.5',
        'rounded-full border px-2.5 py-1',
        'text-xs font-medium leading-none',
        // Transition
        'transition-all duration-150 cursor-pointer',
        // Brand colours per entity type
        meta.accent,
        'text-brand-navy',
        className,
      )}
    >
      {/* Entity type icon */}
      <Icon className="h-3 w-3 shrink-0 opacity-70" strokeWidth={2} />

      {/* Clickable label — opens in new tab */}
      <button
        type="button"
        onClick={handleNavigate}
        className="hover:underline underline-offset-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-cornflower/50 rounded"
        aria-label={`Open ${label} in new tab`}
        title={`Open ${label} in new tab`}
      >
        {label}
      </button>

      {/* External link indicator */}
      <Icons.externalLink
        className="h-2.5 w-2.5 shrink-0 opacity-40"
        strokeWidth={2}
        aria-hidden="true"
      />

      {/* Eye icon — triggers quick preview (if handler provided) */}
      {onPreview && (
        <button
          type="button"
          onClick={handlePreview}
          className={cn(
            'ml-0.5 rounded-full p-0.5',
            'opacity-50 hover:opacity-100 transition-opacity',
            'focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-cornflower/50',
          )}
          aria-label={`Preview ${label}`}
          title={`Preview ${label}`}
        >
          <Icons.eye className="h-3 w-3" strokeWidth={2} />
        </button>
      )}
    </span>
  )
}
